import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Popconfirm, Tag, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { photoTypeDescriptionApi, photoTypeApi } from '../../services/api';

const PhotoTypePresets = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filterType, setFilterType] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [photoTypeOptions, setPhotoTypeOptions] = useState([]);
  const [photoTypeNames, setPhotoTypeNames] = useState({});

  useEffect(() => {
    const loadPhotoTypes = async () => {
      try {
        const res = await photoTypeApi.all();
        const opts = (res.data || []).map(pt => ({ label: pt.name, value: pt.id }));
        setPhotoTypeOptions(opts);
        const names = {};
        (res.data || []).forEach(pt => { names[pt.id] = pt.name; });
        setPhotoTypeNames(names);
      } catch {}
    };
    loadPhotoTypes();
  }, []);

  const fetchData = async (page = 1, photoType = filterType) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (photoType) params.photoType = photoType;
      const res = await photoTypeDescriptionApi.list(params);
      setData(res.data.list);
      setPagination({
        current: res.data.pagination.page,
        pageSize: res.data.pagination.limit,
        total: res.data.pagination.total,
      });
    } catch (err) {
      message.error('获取列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      photoType: record.photo_type,
      description: record.description,
      sortOrder: record.sort_order,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await photoTypeDescriptionApi.update(editing.id, values);
        message.success('更新成功');
      } else {
        await photoTypeDescriptionApi.create(values);
        message.success('创建成功');
      }
      setModalOpen(false);
      fetchData(pagination.current);
    } catch (err) {
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await photoTypeDescriptionApi.delete(id);
      message.success('删除成功');
      fetchData(pagination.current);
    } catch (err) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  const columns = [
    {
      title: '照片类型',
      dataIndex: 'photo_type',
      key: 'photo_type',
      width: 120,
      render: (v) => <Tag color="#8B7355">{photoTypeNames[v] || `类型${v}`}</Tag>,
    },
    {
      title: '描述文本',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="确定删除?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Select
            placeholder="筛选照片类型"
            allowClear
            style={{ width: 160 }}
            options={photoTypeOptions}
            value={filterType}
            onChange={(v) => { setFilterType(v); fetchData(1, v); }}
          />
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ background: '#8B7355' }}>
          新增常用语
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (page) => fetchData(page),
        }}
      />

      <Modal
        title={editing ? '编辑常用语' : '新增常用语'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="photoType" label="照片类型" rules={[{ required: true, message: '请选择照片类型' }]}>
            <Select options={photoTypeOptions} placeholder="请选择照片类型" />
          </Form.Item>
          <Form.Item name="description" label="描述文本" rules={[{ required: true, message: '请输入描述文本' }]}>
            <Input placeholder="请输入常用描述" maxLength={255} />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序序号">
            <Input type="number" placeholder="数字越小越靠前" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PhotoTypePresets;
